// ══════════════════════════════════════════════════════════════════════════════
// SCORING ENGINE — browser adapter over the shared core.
//
// The maths lives in supabase/functions/_shared/scoring-core.ts so the edge
// function and the browser cannot drift apart. This file adds what only makes
// sense in a browser: threshold overrides held in sessionStorage and the
// builders that turn stored deal context into scoring input.
//
// Everything from the core is re-exported, so existing imports keep working.
// ══════════════════════════════════════════════════════════════════════════════

import {
  DEFAULT_THRESHOLDS,
  buildScoringInputFromObject,
  calculateScore as calculateScoreWithThresholds,
  type ScoringThresholds,
  type ScoringInput,
  type ScoringResult,
} from "@shared/scoring-core";

export * from "@shared/scoring-core";

export function getThresholds(): ScoringThresholds {
  try {
    const raw = sessionStorage.getItem("brickshare_thresholds");
    if (raw) return { ...DEFAULT_THRESHOLDS, ...JSON.parse(raw) };
  } catch { /* ignore */ }
  return DEFAULT_THRESHOLDS;
}

/**
 * Score a deal using this browser session's thresholds.
 *
 * Note the asymmetry with the core: these thresholds can be overridden from
 * sessionStorage, which is fine for local what-if exploration but is why the
 * authoritative verdict is produced server-side, where the values cannot be
 * edited from a console.
 */
export function calculateScore(input: ScoringInput): ScoringResult {
  return calculateScoreWithThresholds(input, getThresholds());
}

// ══════════════════════════════════════════════════════════════════════════════
// CONTEXT BUILDERS (browser-bound — the pure mapping lives in the core)
// ══════════════════════════════════════════════════════════════════════════════

// Backward-compatible wrapper that reads from sessionStorage
export function buildScoringInputFromContext(): ScoringInput {
  const raw = sessionStorage.getItem("brickshare_context");
  if (!raw) return {};
  try {
    return buildScoringInputFromObject(JSON.parse(raw));
  } catch {
    return {};
  }
}

// ── Helper: get raw context for assumptions display ──
export function getRawContext(): Record<string, unknown> {
  const raw = sessionStorage.getItem("brickshare_context");
  if (!raw) return {};
  try { return JSON.parse(raw); } catch { return {}; }
}
